import { createFileRoute } from "@tanstack/react-router"
import { AuthenticationFactorType_AllValues } from "@/@client/Theasoft_DataTypes_Enums"
import { useGetUserAuthenticationFactors } from "@/routes/-components/user-authentication-factor/use-get-user-authentication-factors"
import { useSendCodeMutation } from "@/routes/-components/user-authentication-factor/use-send-code-mutation"
import { useGetAuthenticatedUser } from "@/routes/app/-components/use-authenticated-user"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage"
import { CancelButton } from "@components/Buttons"
import { SelectEntity } from "@components/entity-select"
import { FormButtonGroup } from "@components/form-button-group"
import { Stack, Button } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { AuthContainer } from "../-components/auth-container"

export const Route = createFileRoute(
  "/authenticate/mfa/select-authentication-factor"
)({
  component: SelectAuthenticationFactor,
  validateSearch: (search: { returnUrl?: string }) => {
    return {
      returnUrl: search.returnUrl,
    }
  },
})

export function SelectAuthenticationFactor() {
  const search = Route.useSearch()
  const { data: authenticatedUser } = useGetAuthenticatedUser()
  const { data: userAuthenticationFactors } = useGetUserAuthenticationFactors({
    userId: authenticatedUser.userId,
    includeInactive: false,
    includeUnvalidated: false,
    includeNotAllowedAuthenticationTypes: false,
  })
  const [userAuthenticationFactorId, setUserAuthenticationFactorId] =
    useUserLocalStorage<number | undefined>({
      defaultValue: userAuthenticationFactors[0]?.userAuthenticationFactorId,
      key: "user-authentication-factor-id",
    })
  const { mutate: sendTotpCode } = useSendCodeMutation()
  const navigate = Route.useNavigate()
  const { i18n, t } = useTranslation("app_server")
  const selectedAuthFactor =
    userAuthenticationFactors.find(
      (uaf) => uaf.userAuthenticationFactorId === userAuthenticationFactorId
    ) ?? userAuthenticationFactors[0]
  const navigateToValidate = () => {
    if (selectedAuthFactor)
      navigate({
        to: "/authenticate/mfa/$id/validate",
        params: {
          id: selectedAuthFactor.userAuthenticationFactorId.toString(),
        },
        search: { returnUrl: search.returnUrl },
      })
  }
  React.useEffect(() => {
    if (userAuthenticationFactors.length === 0) {
      navigate({
        to: "/authenticate/mfa/create",
        search: { returnUrl: search.returnUrl },
      })
    }
    if (userAuthenticationFactors.length === 1) {
      if (
        selectedAuthFactor?.authenticationFactorType ===
        AuthenticationFactorType_AllValues[1]
      ) {
        sendTotpCode({
          userAuthenticationFactorId:
            selectedAuthFactor.userAuthenticationFactorId,
          language: i18n.language,
        })
      }
      navigateToValidate()
    }
    if (userAuthenticationFactors.length === 0) {
      navigate({
        to: "/authenticate/login",
        search: { returnUrl: search.returnUrl },
      })
    }
  }, [])

  return (
    <AuthContainer
      title={t("UserAuthenticationFactor_SelectAuthenticationFactor")}
      description=""
    >
      <Stack>
        {authenticatedUser ? (
          <SelectEntity
            url="/api/UserAuthenticationFactor/GetUserAuthenticationFactors"
            toRequest={() => ({
              userId: authenticatedUser.userId,
              includeInactive: false,
              includeUnvalidated: false,
              includeNotAllowedAuthenticationTypes: false,
            })}
            getId={(value) => value.userAuthenticationFactorId}
            getLabel={(value) => value.name}
            value={selectedAuthFactor?.userAuthenticationFactorId}
            onChange={(_, entity) =>
              setUserAuthenticationFactorId(entity.userAuthenticationFactorId)
            }
          />
        ) : null}
        <FormButtonGroup>
          <CancelButton
            onClick={() =>
              navigate({
                to: "/authenticate/login",
                search: { returnUrl: search.returnUrl },
              })
            }
          />
          <Button
            disabled={!userAuthenticationFactorId}
            onClick={() => {
              if (userAuthenticationFactorId) {
                selectedAuthFactor?.authenticationFactorType ===
                  AuthenticationFactorType_AllValues[1] &&
                  sendTotpCode({
                    userAuthenticationFactorId:
                      selectedAuthFactor.userAuthenticationFactorId,
                    language: i18n.language,
                  })

                navigateToValidate()
              }
            }}
          >
            {t("Next")}
          </Button>
        </FormButtonGroup>
      </Stack>
    </AuthContainer>
  )
}
